import { useContext, useEffect, useState, type ReactNode } from "react";
import { EigenerKopf } from "../../../components/shell/Logo";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../data";
import { Dialog, useAction } from "../../../components/ui";
import { StaffWorkspaceContext } from "../../../app/session";
import { date } from "../../../lib/format";
import { billingAvailable, loadLegalNotices, loadLifecycle, loadMissingConsents, loadReadOnlyState, loadOwnConsents, openBillingPortal, recordB2bDeclaration, recordConsents } from "./data";
import { B2B_VERSION, LEGAL_DOCS, b2bDeclarationText, b2bMissingText, legalDoc, readOnlyText, testphaseStand, type LegalKind, type LegalNotice, type LifecycleStatus, type PendingKind } from "./model";
import { WEBSITE_URL } from "./parts";
import { kurzDatum, wechselLink } from "../../../model/tarif";
import { useTarifKatalog } from "../../../components/Tarif";
import {
  abbuchungErstZumTestende, intervallVon, lesbarBis, nachTestFenster, neuAnlegenNachTestText, tarifMitPreis, testLeiste, testendeKasten, weiterText,
} from "../../../model/testende";
import type { TarifKatalog } from "../../../model/tarif";
import { t, currentLang } from "../../../lib/sprache";
import { AgenturSperre } from "../../../app/Sperrseite";
import { portalZustand } from "../../../app/sperre-model";
import { cleanVersions, normalizePendingEmail, pendingFor, writePendingEntry, type PendingEntry } from "../../../lib/zustimmung-vormerkung";
import { useRundgang } from "../coco-rundgang";

/**
 * Hinweise im Rahmen des Agentur-Portals: Nur-Lesen-Phase mit Grund (Testphase abgelaufen, gesperrt, gekündigt),
 * vorgemerkte Kündigung und fehlende Zustimmungen zu AGB, Datenschutz und AVV (neue Fassung: erneut zustimmen).
 */
export function AgenturHinweise({ orgId, isChef, timeZone, start, onTestLeiste }: {
  orgId: string; isChef: boolean; timeZone: string;
  /** D-2 (Inhaber 21.10.2026): der nächste Startschritt läuft in derselben schmalen Leiste mit wie der Test (eine Leiste statt zwei) */
  start?: ReactNode;
  /** meldet, ob die Testleiste gerade steht (dann zeigt der Rahmen kein eigenes Startbanner) */
  onTestLeiste?: (da: boolean) => void;
}) {
  const life = useQuery({ queryKey: ["org_lifecycle", orgId], queryFn: () => loadLifecycle(api, orgId), staleTime: 60_000 });
  const missing = useQuery({ queryKey: ["user_consents", "fehlend"], queryFn: () => loadMissingConsents(api), staleTime: 300_000 });
  // 0182: angekündigte Änderungen der Rechtstexte bzw. neue Unterauftragsverarbeiter (nur Admins)
  const notices = useQuery({ queryKey: ["legal_change_notices", "offen"], queryFn: () => loadLegalNotices(api), staleTime: 600_000, enabled: isChef });
  const [nowMs] = useState(() => Date.now());
  // 0216: Test im vollen Umfang (nur Admins): Zeile, einmaliges Hinweisfenster, Hinweis vor und nach dem Testende
  const umfang = useTestumfang(orgId, isChef);
  const voll = isChef && umfang.data?.full ? umfang.data : null;
  const s = life.data;
  // 0323: Preise und Namen der Tarife aus dem Katalog (Leiste im Test, Kasten und Fenster nach dem Test)
  const katalog = useTarifKatalog();
  // K-383: gewählter Tarif mit Preis für die Nur-Lesen-Leiste nach dem Test
  const tarifQ = useQuery({
    queryKey: ["plans", "preis", s?.plan_key ?? ""], enabled: !!s?.read_only && s?.reason === "trial_expired" && !!s?.plan_key,
    queryFn: () => api.select<{ name: string; price_month_cents: number; price_year_cents: number }>("plans", { columns: "name, price_month_cents, price_year_cents", filters: [["key", "eq", s!.plan_key!]] }).then((r) => r[0] ?? null),
  });
  const intervall: "month" | "year" = s?.billing_interval === "year" ? "year" : "month";
  const tarif = tarifQ.data ? { name: tarifQ.data.name, cents: intervall === "year" ? tarifQ.data.price_year_cents : tarifQ.data.price_month_cents, interval: intervall } : null;
  const fmt = (iso: string) => date(iso, timeZone);
  const zahlung = isChef && !!s && zahlungOffen(s);
  const testphase = isChef && !!s ? testphaseHinweis(s, nowMs) : null;
  const ankuendigungen = isChef ? notices.data ?? [] : [];
  // 0323: Fenster nach dem Test nur, wenn im Test etwas außerhalb des gewählten Tarifs genutzt wurde
  const nachTest = isChef && umfang.data?.ended && genutzt(umfang.data).length > 0 ? umfang.data : null;
  // 0323: Leiste im Test für den Admin, mit und ohne Zahlungsart (auch im Tarif Agentur)
  const imTest = isChef && !!s && s.status === "trial" && !!s.trial_ends_at && Date.parse(s.trial_ends_at) > nowMs;
  const testende = isChef && !!s?.read_only && s.reason === "trial_expired";
  // K-573: Ruhe oben, solange der Rundgang mit Coco läuft: Testleiste und Hinweis zum Testumfang (samt Fenster) eingeklappt
  const rundgang = !!useRundgang();
  const testLeisteDa = imTest && !!s && !rundgang;
  useEffect(() => { onTestLeiste?.(testLeisteDa); }, [testLeisteDa, onTestLeiste]);
  if (!s?.read_only && !s?.cancel_effective_at && !(missing.data ?? []).length && !zahlung && !testphase && !ankuendigungen.length && !voll && !nachTest && !imTest) return null;
  return (
    <div className="content tk-rahmen-hinweise">
      {ankuendigungen.length > 0 && <RechtsAnkuendigungen items={ankuendigungen} timeZone={timeZone} />}
      {zahlung && s && <ZahlungOffenHinweis orgId={orgId} s={s} timeZone={timeZone} />}
      {/* Eine Sache nach der anderen (Praxistest K-068): das Fenster erst, wenn Zustimmungen und die Frage nach der Zahlungsart erledigt sind */}
      {testLeisteDa && s && <TestLeiste s={s} d={umfang.data ?? null} katalog={katalog.data ?? null} timeZone={timeZone} nowMs={nowMs} start={start} />}
      {voll && !rundgang && <TestumfangHinweis orgId={orgId} d={voll} timeZone={timeZone}
        mitZahlungsart={!!s && (!!s.stripe_managed || !!s.paypal_managed)}
        fensterErlaubt={!(missing.data ?? []).length && !s?.can_skip_payment && !ankuendigungen.length} />}
      {nachTest && <NachTestFenster orgId={orgId} d={nachTest} katalog={katalog.data ?? null} interval={intervall}
        fensterErlaubt={!(missing.data ?? []).length && !ankuendigungen.length} />}
      {testende && s && <TestendeKasten s={s} d={umfang.data ?? null} katalog={katalog.data ?? null} timeZone={timeZone} />}
      {s?.read_only && !testende && (
        <div className="integrity-banner" role="status">
          <p><strong>{t("Nur lesen.", "Read-only.")}</strong> {readOnlyText(s, fmt, tarif)}</p>
          {isChef && (
            <div className="row">
              <Link className="btn" to="/agentur/einstellungen/export">{t("Daten exportieren", "Export data")}</Link>
              <Link className="btn" to="/agentur/einstellungen/abo">{t("Abo & Tarif", "Subscription & plan")}</Link>
            </div>
          )}
        </div>
      )}
      {s && !s.read_only && s.cancel_effective_at && (
        <div className="notice" role="status">
          {t("Gekündigt zum ", "Terminated as of ")}{fmt(s.cancel_effective_at)}{t(". Bis dahin arbeitet ihr wie gewohnt; danach ist das Portal gesperrt und du kannst deine Daten noch exportieren.", ". Until then you work as usual; afterwards the portal is locked and you can still export your data.")}{isChef && <> <Link to="/agentur/einstellungen/abo">{t("Kündigung ansehen oder zurücknehmen", "View or withdraw termination")}</Link></>}
        </div>
      )}
      <Zustimmungen items={missing.data ?? []} />
    </div>
  );
}

/**
 * Zahlung für das Abo gescheitert (0157): Stripe-Abo „überfällig“ (past_due, bei unpaid zusätzlich gesperrt). Nur für Admin;
 * ein vom Betrieb freigeschaltetes Abo ohne Stripe meldet das Laufzeitende über die Glocke (0092).
 */
export function zahlungOffen(s: Pick<LifecycleStatus, "subscription_status" | "stripe_managed" | "is_chef">): boolean {
  return s.subscription_status === "past_due" && !!s.stripe_managed;
}
/**
 * Hinweis auf die Restdauer der Testphase, solange kein Abo abgeschlossen ist. Seit 13.10.2026 (Inhaber: „einfach immer
 * anzeigen, dass die Probezeit noch so und so viele Tage gültig ist“) über die ganze Testphase; „bald“ ab fünf Tagen vor dem Ende.
 */
export function testphaseHinweis(s: Pick<LifecycleStatus, "status" | "trial_ends_at" | "stripe_managed" | "paypal_managed">, nowMs: number): { tageUebrig: number; bald: boolean } | null {
  // K-389: auch ein PayPal-Probeabo ist eine hinterlegte Zahlungsart (kein „ohne Zahlungsdaten … danach nur lesbar“)
  if (s.status !== "trial" || s.stripe_managed || s.paypal_managed || !s.trial_ends_at) return null;
  const st = testphaseStand(null, s.trial_ends_at, nowMs);
  return st && !st.abgelaufen ? { tageUebrig: st.tageUebrig, bald: st.tageUebrig <= 5 } : null;
}

/**
 * E (K-484): Test mit Zahlungsart bei Stripe, „Nach dem Test automatisch weiterlaufen“ ausgeschaltet. Eine Kündigung in der
 * Testphase beendet den Test sofort (0251); steht das Stripe-Abo im laufenden Test auf „zum Ende gekündigt“, ist es deshalb
 * dieser Schalter (0360).
 */
export function autoWeiterAus(s: Pick<LifecycleStatus, "status" | "stripe_trial" | "cancel_at_period_end" | "read_only"> | null | undefined): boolean {
  return !!s && s.status === "trial" && !s.read_only && !!s.stripe_trial && !!s.cancel_at_period_end;
}

/** Antwort von public.trial_scope (0216) */
export interface Testumfang {
  full: boolean; ended: boolean; until: string | null; plan: string | null; plan_name: string | null; plan_talents: number | null;
  /** 0323: Test im vollen Umfang an, empfohlener Tarif, genutzte Funktionen außerhalb des gewählten Tarifs (mit Zahl) */
  full_scope?: boolean; recommended?: string | null; used?: { de: string[]; en: string[] };
  de: { limits: string[]; modules: string[] }; en: { limits: string[]; modules: string[] };
}
const gemerkt = (art: string, orgId: string): boolean => { try { return window.localStorage.getItem(`co-testumfang:${art}:${orgId}`) === "1"; } catch { return true; } };
const merken = (art: string, orgId: string) => { try { window.localStorage.setItem(`co-testumfang:${art}:${orgId}`, "1"); } catch { /* ohne Speicher erscheint das Fenster beim nächsten Öffnen wieder */ } };

/**
 * Test im vollen Umfang (0216): Jede Testphase läuft mit allen Funktionen, der gewählte Tarif gilt danach. Dauerhaft eine Zeile
 * mit dem Enddatum, einmalig ein Hinweisfenster, ab drei Tagen vor dem Ende die Liste dessen, was mit dem gewählten Tarif wegfällt.
 */
function TestumfangHinweis({ orgId, d, timeZone, mitZahlungsart, fensterErlaubt }: { orgId: string; d: Testumfang; timeZone: string; mitZahlungsart: boolean; fensterErlaubt: boolean }) {
  const [offen, setOffen] = useState(() => !gemerkt("start", orgId));
  const bis = d.until ? date(d.until, timeZone) : "";
  const tarif = d.plan_name ?? "";
  // 0225: keine Erinnerung vor dem Testende (Inhaber 13.10.2026); die Zeile im Test steht seit 0323 in TestLeiste
  const zu = () => { merken("start", orgId); setOffen(false); };
  return (
    <>
      <Dialog open={offen && fensterErlaubt} onClose={zu} title={t("Dein Test läuft jetzt im vollen Umfang", "Your trial now runs in full scope")}>
        <div className="stack">
          <p>
            {d.plan_talents != null && d.plan_talents >= 0
              ? t(`Dein gewählter Tarif ${tarif} ist für bis zu ${d.plan_talents} Creator ausgelegt und enthält nicht alle Funktionen.`, `Your chosen ${tarif} plan is designed for up to ${d.plan_talents} creators and does not include all features.`)
              : t(`Dein gewählter Tarif ${tarif} enthält nicht alle Funktionen.`, `Your chosen ${tarif} plan does not include all features.`)}
            {" "}{t(`Damit du CreatorOffice ohne Einschränkungen kennenlernen kannst, steht dir bis zum ${bis} der volle Funktionsumfang zur Verfügung. ${mitZahlungsart ? `Danach läuft dein Konto im Tarif ${tarif} weiter. Den vollen Umfang behältst du mit einem Tarifwechsel unter Abo & Tarif.` : `Danach ist das Portal gesperrt, bis du unter Abo & Tarif einen Tarif abschließt; es geht nichts verloren, und exportieren kannst du weiterhin.`}`,
              `So that you can get to know CreatorOffice without restrictions, the full feature set is available to you until ${bis}. ${mitZahlungsart ? `After that your account continues on the ${tarif} plan. You keep the full scope by changing your plan under Subscription & plan.` : `After that the portal is locked until you take out a plan under Subscription & plan; nothing is lost, and you can still export.`}`)}
          </p>
          <div className="row">
            <button type="button" className="primary" onClick={zu}>{t("Verstanden", "Got it")}</button>
            <Link className="btn" to="/agentur/einstellungen/abo" onClick={zu}>{t("Tarife ansehen", "View plans")}</Link>
          </div>
        </div>
      </Dialog>
    </>
  );
}

/**
 * L (K-491): Empfehlung aus dem Test für das Kennzeichen „Empfohlen für dich“: der kleinste Tarif, der alles im Test Genutzte
 * abdeckt (trial_scope.recommended), aber nur, wenn er vom gewählten Tarif abweicht. Reicht der gewählte Tarif (etwa bei einem
 * neuen, noch leeren Konto), bleibt es bei „Unsere Empfehlung“ an Team.
 */
export function empfehlungAusTest(d: Pick<Testumfang, "plan" | "recommended"> | null | undefined): string | null {
  return d?.recommended && d.recommended !== d.plan ? d.recommended : null;
}

/** Stand des Testumfangs (public.trial_scope); derselbe Abfrageschlüssel wie die Hinweise oben */
export function useTestumfang(orgId: string, enabled: boolean) {
  return useQuery({ queryKey: ["trial_scope", orgId], enabled, staleTime: 300_000,
    queryFn: async () => { const r = await api.read<unknown>("trial_scope", { p_org: orgId }); return (typeof r === "string" ? JSON.parse(r) : r) as Testumfang; } });
}

/**
 * 4 (0323): Weg „Mit <empfohlener Tarif> weitermachen“ für die Seite „Nur lesen“ beim Neuanlegen nach dem Testende ohne
 * Zahlungsart (nur Admin). Sonst null: dann bleibt der bisherige Hinweis.
 */
export function useTestendeWeiter(orgId: string, s: Pick<LifecycleStatus, "read_only" | "reason" | "plan_key" | "billing_interval"> | null | undefined, isChef: boolean): { label: string; to: string; text: string } | null {
  const aktiv = isChef && !!s?.read_only && s.reason === "trial_expired";
  const umfang = useTestumfang(orgId, aktiv);
  const katalog = useTarifKatalog();
  if (!aktiv) return null;
  const e = tarifMitPreis(umfang.data?.recommended ?? s?.plan_key ?? null, katalog.data ?? null, intervallVon(s?.billing_interval));
  return { label: e ? weiterText(e.name) : t("Tarif wählen", "Choose plan"), to: e ? wechselLink(e.key) : TARIF_WAHL, text: neuAnlegenNachTestText() };
}

/** Genutzte Funktionen außerhalb des gewählten Tarifs (0323) in der Oberflächensprache */
function genutzt(d: Testumfang): string[] {
  return (currentLang() === "en" ? d.used?.en : d.used?.de) ?? [];
}

/**
 * 1 und 2 (0323): Leiste im Test. Ohne Zahlungsart „Zahlungsart hinterlegen“ (der gewählte Tarif ist im Dialog vorgewählt, kein
 * „Upgrade“) und „Tarife ansehen“; mit Zahlungsart der Tarif mit Preis, der danach weiterläuft, und nur wenn im Test Funktionen
 * aus einem größeren Tarif genutzt werden, der Hinweis darauf mit „Tarif ansehen“. Am Handy nur die Kernzeile und die Knöpfe.
 */
function TestLeiste({ s, d, katalog, timeZone, nowMs, start }: { s: LifecycleStatus; d: Testumfang | null; katalog: TarifKatalog | null; timeZone: string; nowMs: number; start?: ReactNode }) {
  const ende = s.trial_ends_at!;
  const intervall = intervallVon(s.billing_interval);
  const mitZahlungsart = !!s.stripe_managed || !!s.paypal_managed;
  const planKey = d?.plan ?? s.plan_key ?? null;
  const gewaehlt = tarifMitPreis(planKey, katalog, intervall);
  const empfohlen = tarifMitPreis(d?.recommended ?? null, katalog, intervall);
  const st = testphaseStand(null, ende, nowMs);
  // 0205: solange die Frage nach der Zahlungsart offen ist, nur die Kernzeile (wie bisher); mit Zahlungsart ohne Resttage (Vorschlag 2)
  const kurz = !!s.can_skip_payment;
  // E (K-484): Test mit Zahlungsart, „Nach dem Test automatisch weiterlaufen“ aus: endet ohne Kosten
  const ohneKosten = autoWeiterAus(s);
  const x = testLeiste({ bis: kurzDatum(ende, timeZone), tage: kurz || mitZahlungsart || !st ? null : st.tageNoch, vollerUmfang: !!d?.full_scope, mitZahlungsart,
    abbuchungSpaeter: abbuchungErstZumTestende(ende, nowMs), gewaehlt, empfohlen, endetOhneKosten: ohneKosten });
  // D-2 (Inhaber 21.10.2026): eine schmale Leiste (höchstens 40 px) statt Kasten mit Knopfzeile; der Weg als Textlink rechts,
  // daneben der nächste Startschritt. Der volle Satz steht im Tooltip, wenn er nicht in die Zeile passt.
  const satz = [`${x.titel}${x.tage}${x.tage || !x.titel.endsWith(".") ? "." : ""}`, !kurz ? x.text : "", x.hinweis ?? ""].filter(Boolean).join(" ");
  return (
    <div className="tk-leiste tk-testzeile" role="status">
      <p className="tk-leiste-text" title={satz}><strong>{x.titel}</strong>{x.tage}{x.tage || !x.titel.endsWith(".") ? ". " : " "}
        {/* Am Handy nur die Kernzeile (Inhaber 13.10.2026: zu viel Text über der Seite) */}
        {!kurz && <span className={mitZahlungsart ? undefined : "tk-nur-breit"}>{x.text}</span>}
        {x.hinweis && <> {x.hinweis}</>}</p>
      {/* K-478: ein Weg je Lage; „Tarife ansehen“ führte auf dieselbe Seite wie „Zahlungsart hinterlegen“ und entfällt */}
      {mitZahlungsart
        ? ohneKosten
          ? <Link className="tk-leiste-link" to="/agentur/einstellungen/abo#testphase-title">{t("Automatisch weiterlaufen einschalten", "Switch automatic renewal on")}</Link>
          : x.hinweis && empfohlen && <Link className="tk-leiste-link" to={wechselLink(empfohlen.key)}>{t("Tarif ansehen", "View plan")}</Link>
        : <Link className="tk-leiste-link tk-leiste-link-stark" to={planKey ? wechselLink(planKey) : "/agentur/einstellungen/abo"}>{t("Zahlungsart hinterlegen", "Add payment method")}</Link>}
      {start}
    </div>
  );
}

/**
 * 3 (0323): Testende ohne Zahlungsart, Kasten oben (nur Admin). Weitermachen mit dem empfohlenen Tarif (Dialog mit diesem Tarif
 * vorgewählt, dann Checkout), anderer Tarif, klein der Export. Kein Löschen hier; Kündigen und Löschen bleiben in den Einstellungen.
 */
function TestendeKasten({ s, d, katalog, timeZone }: { s: LifecycleStatus; d: Testumfang | null; katalog: TarifKatalog | null; timeZone: string }) {
  const intervall = intervallVon(s.billing_interval);
  const empfohlen = tarifMitPreis(d?.recommended ?? s.plan_key ?? null, katalog, intervall);
  const bis = lesbarBis(s.plan);
  const k = testendeKasten({ empfohlen, bis: bis ? date(bis, timeZone) : null });
  return (
    <div className="integrity-banner tk-testende" role="status">
      <p><strong>{k.titel}</strong> {k.text}</p>
      <div className="row">
        {empfohlen && k.weiter && <Link className="btn primary" to={wechselLink(empfohlen.key)}>{k.weiter}</Link>}
        <Link className="btn" to={TARIF_WAHL}>{t("Anderen Tarif wählen", "Choose another plan")}</Link>
        <small><Link to="/agentur/einstellungen/export">{t("Daten exportieren", "Export data")}</Link></small>
      </div>
      {k.klein && <small>{k.klein}</small>}
    </div>
  );
}
/** Abo & Tarif mit geöffnetem Tarifdialog ohne Vorauswahl (0323, „Anderen Tarif wählen“) */
const TARIF_WAHL = "/agentur/einstellungen/abo?wechsel=wahl";

/**
 * 5 (0323): Nach dem Testende, wenn das Konto im gewählten kleineren Tarif weiterläuft und im Test Funktionen außerhalb des Tarifs
 * genutzt wurden: einmaliges Fenster mit genau diesen Funktionen (mit Zahl), dem empfohlenen Tarif und der Differenz. „… behalten“
 * öffnet den Wechseldialog mit Vorschau des anteiligen Betrags; „Mit … weiter“ schließt.
 */
function NachTestFenster({ orgId, d, katalog, interval, fensterErlaubt }: { orgId: string; d: Testumfang; katalog: TarifKatalog | null; interval: "month" | "year"; fensterErlaubt: boolean }) {
  const [offen, setOffen] = useState(() => !gemerkt("ende", orgId));
  // K-432: „gesehen“ nur nach einer Entscheidung (Knopf); Schließen blendet nur für diesen Besuch aus
  const zu = () => { merken("ende", orgId); setOffen(false); };
  const ausblenden = () => setOffen(false);
  const gewaehlt = tarifMitPreis(d.plan, katalog, interval) ?? { key: d.plan ?? "", name: d.plan_name ?? "", cents: null, interval };
  const empfohlen = tarifMitPreis(d.recommended, katalog, interval);
  const f = nachTestFenster({ gewaehlt, empfohlen });
  return (
    <Dialog open={offen && fensterErlaubt} onClose={ausblenden} title={f.titel}>
      <div className="stack">
        <p>{f.einleitung}</p>
        <ul>
          {genutzt(d).map((x) => <li key={x}>{x}</li>)}
        </ul>
        <p>{f.schluss}</p>
        <div className="row">
          {f.behalten && empfohlen
            ? <Link className="btn primary" to={wechselLink(empfohlen.key)} onClick={zu}>{f.behalten}</Link>
            : <Link className="btn" to="/agentur/einstellungen/abo" onClick={zu}>{t("Tarife ansehen", "View plans")}</Link>}
          <button type="button" onClick={zu}>{f.weiter}</button>
        </div>
      </div>
    </Dialog>
  );
}

/** Hinweisbalken bei gescheiterter Zahlung mit dem Weg ins Stripe-Kundenportal (Zahlungsart aktualisieren, Rechnung bezahlen). */
function ZahlungOffenHinweis({ orgId, s, timeZone }: { orgId: string; s: LifecycleStatus; timeZone: string }) {
  const { busy, error, run } = useAction();
  const seit = s.payment_failed_at ? t(` am ${date(s.payment_failed_at, timeZone)}`, ` on ${date(s.payment_failed_at, timeZone)}`) : "";
  // 0590: Abo auf Rechnung: nicht „fehlgeschlagen“, sondern überfällig; der Weg ist die Überweisung (Bankverbindung unter Abo & Tarif)
  if (s.collection_method === "send_invoice") {
    return (
      <div className="integrity-banner" role="alert">
        <p><strong>{t("Rechnung überfällig.", "Invoice overdue.")}</strong> {s.read_only
          ? t("Eine Rechnung für euer Abo ist nach der Fälligkeit offen geblieben, die Agentur ist deshalb nur lesbar. Sobald die Überweisung eingegangen ist, ist alles wieder wie vorher.",
            "An invoice for your subscription remained unpaid after it was due, so the agency is read-only. As soon as the transfer has arrived, everything is as before.")
          : t("Eine Rechnung für euer Abo ist fällig und noch nicht bezahlt. Bitte überweise den offenen Betrag mit der Rechnungsnummer als Verwendungszweck. Bleibt sie länger als 14 Tage offen, ist die Agentur danach nur lesbar.",
            "An invoice for your subscription is due and not yet paid. Please transfer the open amount with the invoice number as the payment reference. If it stays unpaid for more than 14 days, the agency becomes read-only.")}</p>
        <div className="row">
          <Link className="btn primary" to="/agentur/einstellungen/abo#rechnungen">{t("Rechnung und Bankverbindung", "Invoice and bank details")}</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="integrity-banner" role="alert">
      <p><strong>{t("Zahlung fehlgeschlagen.", "Payment failed.")}</strong> {s.read_only
        ? t(`Die Zahlung für euer Abo ist${seit} fehlgeschlagen, das Abo ist deshalb gesperrt; die Agentur ist nur lesbar. Sobald die offene Rechnung bezahlt ist, ist alles wieder wie vorher.`,
          `The payment for your subscription failed${seit}, so the subscription is suspended; the agency is read-only. As soon as the open invoice is paid, everything is as before.`)
        : t(`Die Zahlung für euer Abo ist${seit} fehlgeschlagen. Wir versuchen es in den nächsten Tagen erneut; bitte aktualisiere die Zahlungsart. Scheitern auch die weiteren Versuche, ist die Agentur danach nur lesbar.`,
          `The payment for your subscription failed${seit}. We will retry over the next days; please update the payment method. If the further attempts also fail, the agency becomes read-only.`)}</p>
      <div className="row">
        {s.billing_portal && billingAvailable(api) && (
          <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "portal"}
            onClick={() => void run("portal", async () => { window.location.assign(await openBillingPortal(api, orgId)); })}>
            {busy === "portal" ? t("Kundenportal wird geöffnet …", "Opening customer portal …") : t("Rechnungen und Zahlungsart verwalten", "Manage invoices and payment method")}
          </button>
        )}
        <Link className="btn" to="/agentur/einstellungen/abo">{t("Abo & Tarif", "Subscription & plan")}</Link>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
    </div>
  );
}

type Pending = { acceptedOn: string; kinds: PendingKind[] };

/**
 * Bei der Registrierung angekreuzt: Tag, Dokumente und Person (Mailadresse) merken und nach der Anmeldung mit der Fassung
 * eintragen, die die Datenbank dann nennt (consent_missing). Eine Fassung, die nach dem Ankreuzen erschienen ist, fragt
 * die Seite neu ab. So braucht die Registrierung keine Funktion ohne Anmeldung.
 * Ohne Mailadresse und ohne Sitzung hängt der Eintrag an niemandem und wird nie eingetragen (G-WEB-4).
 */
export function rememberPendingConsent(email?: string | null, kinds: readonly PendingKind[] = ["agency_terms", "privacy", "dpa"], versions: Record<string, string> | null = null): void {
  const s = api.session();
  writePendingEntry({
    acceptedOn: new Date().toLocaleDateString("sv-SE"),
    kinds: [...kinds],
    email: normalizePendingEmail(email ?? s?.email),
    userId: email ? null : s?.userId ?? null,
    savedAt: Date.now(),
    method: "registration",
    // 0412 (Befund 5a): die beim Ankreuzen geltenden Fassungen
    versions: cleanVersions(versions),
  });
}
/** Vormerkung der angemeldeten Person (gleiche Person, nicht älter als eine Woche); sonst nichts. */
function pendingConsent(): (Pending & { entry: PendingEntry }) | null {
  const entry = pendingFor(api.session());
  if (!entry) return null;
  // Vor 0057 hieß die AGB-Zustimmung „terms“
  return { acceptedOn: entry.acceptedOn, kinds: entry.kinds.map((k) => (k === "terms" ? "agency_terms" : k) as PendingKind), entry };
}
function storePending(p: (Pending & { entry: PendingEntry }) | null): void {
  writePendingEntry(p ? { ...p.entry, kinds: [...p.kinds] } : null);
}
/**
 * Gemerkte Zustimmungen eintragen, soweit die fehlende Fassung nicht jünger ist als das Ankreuzen; true, wenn etwas eingetragen wurde.
 * Mit Weg (Registrierung bzw. Google) und, sobald bekannt, dem Kunden (0182). Die B2B-Bestätigung gilt sofort (ihr Wortlaut
 * steht in der App und hat die Fassung B2B_VERSION).
 */
export async function settlePendingConsents(items?: readonly { kind: LegalKind; version: string }[], opts: { orgId?: string | null } = {}): Promise<boolean> {
  const pending = pendingConsent();
  if (!pending) return false;
  const method = pending.entry.method ?? "registration";
  let kinds = pending.kinds;
  let done = false;
  if (kinds.includes("b2b_declaration")) {
    await recordB2bDeclaration(api, method, opts.orgId);
    kinds = kinds.filter((k) => k !== "b2b_declaration");
    storePending({ ...pending, kinds });
    done = true;
  }
  const legal = kinds.filter((k): k is LegalKind => k !== "b2b_declaration");
  if (!legal.length) return done;
  const list = items ?? (await loadMissingConsents(api));
  // 0412 (Befund 5a): gemerkte Fassung vom Ankreuzen, sonst wie bisher der Tag des Ankreuzens
  const gemerkt = pending.entry.versions ?? null;
  const due = list.filter((i) => legal.includes(i.kind) && (gemerkt?.[i.kind] ? gemerkt[i.kind] === i.version : i.version <= pending.acceptedOn));
  if (!due.length) return done;
  await recordConsents(api, due, { method, orgId: opts.orgId });
  storePending({ ...pending, kinds: kinds.filter((k) => !due.some((d) => d.kind === k)) });
  return true;
}

/**
 * Pflicht-Checkbox „als Unternehmer“ (Anwaltsvorgabe 06.10.2026): eigene Checkbox, nie vorangekreuzt, bei Registrierung,
 * Checkout und erneuter Zustimmung zu den AGB ohne gespeicherte Bestätigung.
 */
export function B2bBestaetigung({ id, checked, onChange, error }: { id: string; checked: boolean; onChange: (v: boolean) => void; error?: string | null }) {
  return (
    <>
      <label className="row tk-check">
        <input id={id} type="checkbox" required checked={checked} onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-fehler` : undefined} />
        <span>{b2bDeclarationText()}</span>
      </label>
      {error && <small id={`${id}-fehler`} className="tk-error">{error}</small>}
    </>
  );
}

/** Zustimmung zu AGB und AVV, Kenntnisnahme der Datenschutzhinweise (wie Registrieren.tsx), nicht vorangekreuzt */
export function RechtstexteZustimmung({ id, checked, onChange, error }: { id: string; checked: boolean; onChange: (v: boolean) => void; error?: string | null }) {
  const link = (kind: LegalKind) => `${WEBSITE_URL}${legalDoc(kind)!.path}`;
  return (
    <>
      <label className="row tk-check">
        <input id={id} type="checkbox" required checked={checked} onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-fehler` : undefined} />
        <span>
          {t("Ich akzeptiere die ", "I accept the ")}<a href={link("agency_terms")} target="_blank" rel="noreferrer">{t("AGB", "Terms")}</a> {t("und den ", "and the ")}<a href={link("dpa")} target="_blank" rel="noreferrer">{t("Auftragsverarbeitungsvertrag", "data processing agreement")}</a> {t("und habe die ", "and have read the ")}<a href={link("privacy")} target="_blank" rel="noreferrer">{t("Datenschutzhinweise", "Privacy notice")}</a> {t("gelesen (jeweils in der heute geltenden Fassung).", "(each in the version valid today).")}</span>
      </label>
      {error && <small id={`${id}-fehler`} className="tk-error">{error}</small>}
    </>
  );
}

/** Ausgeblendete Ankündigungen (nur auf diesem Gerät; die Seite Abo & Tarif zeigt sie weiter) */
const HIDDEN_NOTICES_KEY = "tk-rechtshinweise-gelesen";
function hiddenNotices(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(HIDDEN_NOTICES_KEY) ?? "[]") as unknown;
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
}
function hideNotice(id: string): void {
  try {
    localStorage.setItem(HIDDEN_NOTICES_KEY, JSON.stringify([...hiddenNotices().filter((x) => x !== id), id].slice(-50)));
  } catch {
    /* ohne Speicher bleibt der Hinweis sichtbar */
  }
}

/** Sätze zu einer angekündigten Änderung (Portal; die Mail sagt dasselbe, app.legal_notice_texts in 0182) */
export function rechtsAnkuendigungText(n: LegalNotice, fmt: (iso: string) => string): { titel: string; text: string } {
  const doc = legalDoc(n.kind);
  const name = doc ? t(doc.label, doc.labelEn) : n.kind;
  const summary = t(n.summary_de, n.summary_en);
  const frist = n.objection_until ? fmt(n.objection_until) : fmt(n.valid_from);
  if (n.notice_type === "subprocessor") {
    return {
      titel: t("Neuer Unterauftragsverarbeiter", "New subprocessor"),
      text: t(`${summary} Der Einsatz beginnt frühestens am ${fmt(n.valid_from)}. Aus wichtigem Grund kannst du bis zum ${frist} per Mail an ${n.privacy_email} widersprechen.`,
        `${summary} The new subprocessor will be used from ${fmt(n.valid_from)} at the earliest. For good cause you can object until ${frist} by email to ${n.privacy_email}.`),
    };
  }
  const base = t(`${summary} Die neue Fassung gilt ab dem ${fmt(n.valid_from)}; ab dann bitten wir dich hier um deine Zustimmung.`,
    `${summary} The new version applies from ${fmt(n.valid_from)}; from then on we will ask for your consent here.`);
  const kuendigung = n.material
    ? t(` Ist die Änderung für dich erheblich nachteilig, kannst du den Vertrag zum ${fmt(n.valid_from)} kündigen (Sonderkündigungsrecht).`,
      ` If the change is significantly disadvantageous for you, you can terminate the contract with effect from ${fmt(n.valid_from)} (special right of termination).`)
    : "";
  return {
    titel: n.new_version ? t(`Angekündigt: ${name}, neue Fassung vom ${fmt(n.new_version)}`, `Announced: ${name}, new version of ${fmt(n.new_version)}`) : t(`Angekündigt: ${name}`, `Announced: ${name}`),
    text: base + kuendigung,
  };
}

/**
 * 0436 (rechtliche Selbstprüfung 23.10.2026): Admins können einer angekündigten Fassung schon vor dem Gültig-ab-Datum zustimmen
 * (consent_record nimmt die Fassung einer offenen Ankündigung an). Der Wortlaut von Knopf und Satz ist ein Vorschlag und braucht
 * die Freigabe des Inhabers; bis dahin bleibt der Schalter aus und der Hinweis unverändert.
 */
export const FREIWILLIGE_ZUSTIMMUNG_FREIGEGEBEN = false;
/** Ankündigung, der ein Admin freiwillig vorab zustimmen kann: geänderte AGB, AVV oder Datenschutzhinweise mit neuer Fassung */
export function freiwilligZustimmbar(n: Pick<LegalNotice, "notice_type" | "kind" | "new_version">): n is Pick<LegalNotice, "notice_type" | "kind"> & { new_version: string } {
  return n.notice_type === "change" && !!n.new_version && (n.kind === "agency_terms" || n.kind === "dpa" || n.kind === "privacy");
}
/** Vorschlag (nicht freigegeben): Satz und Knopf für die freiwillige Zustimmung im Hinweis */
export function freiwilligText(n: LegalNotice, fmt: (iso: string) => string): { satz: string; knopf: string } {
  return {
    satz: t(`Du kannst ihr schon jetzt zustimmen; dann gilt sie für deine Agentur ab deiner Zustimmung statt ab dem ${fmt(n.valid_from)}.`,
      `You can agree to it now; it then applies to your agency from your consent instead of from ${fmt(n.valid_from)}.`),
    knopf: t("Jetzt zustimmen", "Agree now"),
  };
}

/** Hinweis im Portal auf angekündigte Änderungen (0182): bis zum Gültig-ab-Datum bzw. Ende der Widerspruchsfrist, je Gerät ausblendbar. */
export function RechtsAnkuendigungen({ items, timeZone, freigegeben = FREIWILLIGE_ZUSTIMMUNG_FREIGEGEBEN }: { items: LegalNotice[]; timeZone: string; freigegeben?: boolean }) {
  const [hidden, setHidden] = useState<string[]>(() => hiddenNotices());
  const [zustimmen, setZustimmen] = useState<LegalNotice | null>(null);
  const fmt = (iso: string) => date(iso, timeZone);
  const show = items.filter((n) => !hidden.includes(n.id));
  if (!show.length) return null;
  return (
    <>
      {show.map((n) => {
        const x = rechtsAnkuendigungText(n, fmt);
        const freiwillig = freigegeben && freiwilligZustimmbar(n) ? freiwilligText(n, fmt) : null;
        return (
          <div key={n.id} className="notice stack" role="region" aria-label={x.titel}>
            <strong>{x.titel}</strong>
            <p>{x.text}{freiwillig && ` ${freiwillig.satz}`}</p>
            <div className="row">
              {freiwillig && <button type="button" className="primary" onClick={() => setZustimmen(n)}>{freiwillig.knopf}</button>}
              {n.url && <a className="btn" href={n.url} target="_blank" rel="noreferrer">{t("Neue Fassung ansehen", "View new version")}</a>}
              <Link className="btn" to="/agentur/einstellungen/abo#vertraege">{t("Verträge und Rechtstexte", "Contracts and legal texts")}</Link>
              <button type="button" className="quiet" onClick={() => { hideNotice(n.id); setHidden(hiddenNotices()); }}>{t("Ausblenden", "Hide")}</button>
            </div>
          </div>
        );
      })}
      {zustimmen && freiwilligZustimmbar(zustimmen) && (
        <ZustimmungDialog items={[{ kind: zustimmen.kind as LegalKind, version: zustimmen.new_version }]} offen onClose={() => setZustimmen(null)} />
      )}
    </>
  );
}

/**
 * Zustimmungshinweis für neue Fassungen (Agentur- und Creator-Portal): AGB und AVV nur für Admin, Nutzungsbedingungen
 * für Manager und Creator, Datenschutzhinweise für alle (consent_missing entscheidet, 0057).
 */
export function Zustimmungen({ items, lang = currentLang() }: { items: { kind: LegalKind; version: string }[]; lang?: "de" | "en" }) {
  const qc = useQueryClient();
  // G3 (D-1, Inhaber 21.10.2026): schmale Leiste über der Seite, Texte und Häkchen im Dialog
  const [offen, setOffen] = useState(false);

  // Bei der Registrierung angekreuzte Dokumente gelten, wenn die Fassung beim Ankreuzen schon galt
  useEffect(() => {
    if (!items.length) return;
    void settlePendingConsents(items).then((done) => {
      if (done) void qc.invalidateQueries({ queryKey: ["user_consents"] });
    }).catch(() => undefined);
  }, [items, qc]);

  if (!items.length) return null;
  const en = lang === "en";
  const docs = LEGAL_DOCS.filter((d) => items.some((i) => i.kind === d.kind));
  const versionOf = (k: LegalKind) => items.find((i) => i.kind === k)?.version ?? "";
  const kurz = docs.map((d) => `${en ? d.labelEn : d.label} (${date(versionOf(d.kind))})`).join(", ");
  return (
    <div className="notice row tk-zustimmung-leiste" role="region" aria-labelledby="zustimmung-titel">
      <span className="tk-zustimmung-text"><strong id="zustimmung-titel">{en ? "Please confirm the current versions" : "Bitte bestätige die aktuellen Fassungen"}</strong>
        <span className="muted"> · {kurz}</span></span>
      <button type="button" className="primary" onClick={() => setOffen(true)}>{en ? "Read and agree" : "Lesen und zustimmen"}</button>
      <ZustimmungDialog items={items} lang={lang} offen={offen} onClose={() => setOffen(false)} />
    </div>
  );
}

/**
 * Dialog der Zustimmung (Texte, Häkchen, B2B-Bestätigung bei Bedarf): aus der Leiste der fehlenden Zustimmungen und (0436) aus dem
 * Hinweis einer Ankündigung (freiwillig vorab). Nach dem Speichern verschwinden Leiste bzw. Hinweis (Abfragen neu laden).
 */
function ZustimmungDialog({ items, lang = currentLang(), offen, onClose }: { items: { kind: LegalKind; version: string }[]; lang?: "de" | "en"; offen: boolean; onClose: () => void }) {
  const ws = useContext(StaffWorkspaceContext);
  const creatorKonto = !!ws?.isCreator;
  // 0439 (T-3): Zustimmung für die Agentur des Arbeitsbereichs (user_consents.customer_org_id); die Sperre-Regel gilt je Agentur
  const orgId = ws?.membership.org_id ?? null;
  const qc = useQueryClient();
  const [checked, setChecked] = useState(false);
  const [b2b, setB2b] = useState(false);
  const { busy, error, run } = useAction();
  // Erneute Zustimmung zu den AGB ohne gespeicherte B2B-Bestätigung (Bestand vor 0182): eigene Pflicht-Checkbox dazu
  const agb = items.some((i) => i.kind === "agency_terms");
  const eigene = useQuery({ queryKey: ["user_consents", "eigene"], queryFn: () => loadOwnConsents(api), staleTime: 300_000, enabled: agb });
  const b2bFehlt = agb && !!eigene.data && !eigene.data.some((c) => c.kind === "b2b_declaration" && c.version === B2B_VERSION);
  const en = lang === "en";
  const docs = LEGAL_DOCS.filter((d) => items.some((i) => i.kind === d.kind));
  const versionOf = (k: LegalKind) => items.find((i) => i.kind === k)?.version ?? "";
  return (
      <Dialog open={offen} onClose={onClose} title={en ? "Please confirm the current versions" : "Bitte bestätige die aktuellen Fassungen"}>
      <div className="stack">
      <p>
        {docs.map((d, i) => (
          <span key={d.kind}>
            {i > 0 && (i === docs.length - 1 ? (en ? " and " : " und ") : ", ")}
            <a href={`${WEBSITE_URL}${d.path}`} target="_blank" rel="noreferrer">{en ? d.labelEn : d.label}</a> ({en ? "version of" : "Fassung vom"} {date(versionOf(d.kind))})
          </span>
        ))}
        .{items.some((i) => i.kind === "dpa") && (creatorKonto
          ? t(" Den Auftragsverarbeitungsvertrag schließt du für dein Unternehmen ab: CreatorOffice verarbeitet die Daten deiner Marken und Kontakte in deinem Auftrag.", " You conclude the data processing agreement for your business: CreatorOffice processes the data of your brands and contacts on your behalf.")
          : t(" Den Auftragsverarbeitungsvertrag schließt du als Admin für deine Agentur ab: CreatorOffice verarbeitet die Daten deiner Creator und Marken in deinem Auftrag.", " As admin you conclude the data processing agreement for your agency: CreatorOffice processes the data of your creators and brands on your behalf."))}
        {items.some((i) => i.kind === "agency_terms") && (creatorKonto
          ? t(" Die AGB gelten für deinen Vertrag mit CreatorOffice.", " The terms apply to your contract with CreatorOffice.")
          : t(" Die AGB gelten für den Vertrag deiner Agentur mit CreatorOffice.", " The terms apply to your agency's contract with CreatorOffice."))}
        {/* K-510 b (Inhaber 16.10.2026): was sich geändert hat, wenn eine neue Fassung ohne Ankündigungsmail gilt (0363) */}
        {items.some((i) => i.kind === "agency_terms" && i.version === "2026-10-16") && t(
          " Geändert hat sich nur der Abschnitt Testphase, zu deinen Gunsten: Ein Test endet auch mit hinterlegter Zahlungsart ohne Kosten, wenn du das Weiterlaufen abschaltest.",
          " Only the trial section has changed, in your favour: a trial also ends at no cost with a stored payment method if you switch off automatic renewal.")}
      </p>
      <label className="row tk-check">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>{en ? "I have read the texts and agree." : "Ich habe die Texte gelesen und stimme zu."}</span>
      </label>
      {b2bFehlt && <B2bBestaetigung id="zustimmung-b2b" checked={b2b} onChange={setB2b} />}
      {error && <small className="tk-error" role="alert">{error}</small>}
      <div className="row">
        <button className="primary" type="button" disabled={!checked || (b2bFehlt && !b2b) || !!busy} aria-busy={!!busy}
          onClick={() => void run("zustimmung", async () => {
            if (b2bFehlt && !b2b) throw new Error(b2bMissingText());
            if (b2bFehlt) await recordB2bDeclaration(api, "banner", orgId);
            await recordConsents(api, items, { method: "banner", orgId });
            const p = pendingConsent();
            if (p) storePending({ ...p, kinds: p.kinds.filter((k) => !items.some((i) => i.kind === k)) });
            await qc.invalidateQueries({ queryKey: ["user_consents"] });
            await qc.invalidateQueries({ queryKey: ["legal_change_notices"] });
            onClose();
          }, en ? "Consent saved." : "Zustimmung gespeichert.")}>
          {busy ? (en ? "Saving …" : "Wird gespeichert …") : en ? "Agree" : "Zustimmen"}
        </button>
      </div>
      </div>
      </Dialog>
  );
}

/** Zustimmungshinweis im Creator-Portal (Befund 11): neue Fassungen der Nutzungsbedingungen und Datenschutzhinweise. */
export function CreatorZustimmungen({ lang }: { lang: "de" | "en" }) {
  const missing = useQuery({ queryKey: ["user_consents", "fehlend"], queryFn: () => loadMissingConsents(api), staleTime: 300_000 });
  const items = (missing.data ?? []).filter((i) => i.kind === "user_terms" || i.kind === "privacy");
  if (!items.length) return null;
  return <div className="content tk-rahmen-hinweise"><Zustimmungen items={items} lang={lang} /></div>;
}

/**
 * Manager einer gesperrten oder gekündigten Agentur: kein Zugriff, aber der Grund ist klar. 0425: Vertrag beendet → Sperrseite
 * „Das Abo deiner Agentur ist beendet“ mit dem Hinweis, dass der Admin (Kontoinhaber) exportiert; sonst (offene Zahlung, Sperre
 * durch den Betrieb) wie bisher „Nur lesen, nur für den Admin“.
 */
export function GesperrtHinweis({ orgId }: { orgId: string }) {
  const life = useQuery({ queryKey: ["org_lifecycle", orgId], queryFn: () => loadLifecycle(api, orgId) });
  const ro = useQuery({ queryKey: ["org_read_only", orgId], staleTime: 60_000, queryFn: () => loadReadOnlyState(api, orgId) });
  const [busy, setBusy] = useState(false);
  const s = life.data;
  const abmeldenKnopf = <div className="row"><button type="button" disabled={busy} onClick={() => { setBusy(true); void api.signOut().finally(() => setBusy(false)); }}>{busy ? t("Wird abgemeldet …", "Signing out …") : t("Abmelden", "Sign out")}</button></div>;
  if (portalZustand(ro.data) === "locked") {
    return (
      <>
        <EigenerKopf />
        <main id="main" className="content">
          <AgenturSperre orgId={orgId} orgName={null} life={s} chef={false} fmt={(iso) => date(iso)} trial={!!ro.data?.trial} />
          {/* J4 (Konto-Prüfung 03.11.2026): auch bei gesperrtem Portal eigene Daten und Kontolöschung */}
          <div className="panel stack"><EigenesKontoBeiSperre /></div>
          {abmeldenKnopf}
        </main>
      </>
    );
  }
  const reason = s?.reason === "trial_expired" ? t("Die Testphase deiner Agentur ist abgelaufen.", "Your agency's trial has expired.")
    : s?.reason === "cancelled" ? t(`Deine Agentur hat CreatorOffice gekündigt${s.cancel_effective_at ? t(` (zum ${date(s.cancel_effective_at)})`, ` (as of ${date(s.cancel_effective_at)})`) : ""}.`, `Your agency has terminated CreatorOffice${s.cancel_effective_at ? t(` (zum ${date(s.cancel_effective_at)})`, ` (as of ${date(s.cancel_effective_at)})`) : ""}.`)
    : s?.reason === "suspended" ? t("Deine Agentur ist gesperrt.", "Your agency is suspended.") : t("Deine Agentur nutzt CreatorOffice derzeit nicht.", "Your agency is currently not using CreatorOffice.");
  return (
    <>
    <EigenerKopf />
    <main id="main" className="content">
      <div className="panel stack tk-empty" role="alert">
        <h1 className="tk-state-title">{t("Kein Zugang zum Portal der Agentur", "No access to the agency portal")}</h1>
        <p>{reason} {t("Solange die Agentur nur lesbar ist, lesen Manager mit; ist ihr Vertrag beendet, liest und exportiert nur der Admin die Daten. Fragen zur Agentur beantwortet dein Admin.", "While the agency is read-only, managers can read along; once its contract has ended, only the admin reads and exports the data. Your admin answers questions about the agency.")}</p>
        <div className="row">
          <button type="button" disabled={busy} onClick={() => { setBusy(true); void api.signOut().finally(() => setBusy(false)); }}>{busy ? t("Wird abgemeldet …", "Signing out …") : t("Abmelden", "Sign out")}</button>
        </div>
        {/* J4 (Konto-Prüfung 03.11.2026): keine Sackgasse: eigene Daten herunterladen und das eigene Konto löschen lassen */}
        <EigenesKontoBeiSperre />
      </div>
    </main>
    </>
  );
}

/**
 * J4 (Konto-Prüfung 03.11.2026): Manager einer gesperrten oder gekündigten Agentur sahen nur „Abmelden“. Jetzt laden sie ihre
 * eigenen Daten herunter (my_data_export) und lassen ihr Konto löschen (account_delete_request beendet die Mitgliedschaft und
 * schließt die Anmeldung, wenn sonst nichts daran hängt).
 */
function EigenesKontoBeiSperre() {
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState<"" | "daten" | "loeschen">("");
  const [fehler, setFehler] = useState<string | null>(null);
  const daten = async () => {
    setBusy("daten");
    setFehler(null);
    try {
      const inhalt = await api.read<unknown>("my_data_export", {});
      const url = URL.createObjectURL(new Blob([JSON.stringify(inhalt, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "creatoroffice-meine-daten.json";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t("Das hat gerade nicht geklappt.", "That did not work just now."));
    } finally {
      setBusy("");
    }
  };
  const loeschen = async () => {
    setBusy("loeschen");
    setFehler(null);
    try {
      await api.rpc("account_delete_request", { p_reason: null });
      await api.signOut({ everywhere: true }).catch(() => undefined);
      window.location.assign("/anmelden");
    } catch (e) {
      setFehler(e instanceof Error ? e.message : t("Das hat gerade nicht geklappt.", "That did not work just now."));
      setBusy("");
    }
  };
  return (
    <section className="stack" aria-label={t("Eigenes Konto", "Own account")}>
      <h2 className="tk-state-title">{t("Dein eigenes Konto", "Your own account")}</h2>
      <p className="muted">{t("Deine eigenen Daten kannst du jederzeit herunterladen. Willst du das Konto nicht mehr nutzen, lässt du es löschen; die Daten der Agentur bleiben beim Admin.",
        "You can download your own data at any time. If you no longer want to use the account, have it deleted; the agency's data stays with the admin.")}</p>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
      {!offen ? (
        <div className="row">
          <button type="button" disabled={!!busy} aria-busy={busy === "daten"} onClick={() => void daten()}>{busy === "daten" ? t("Wird vorbereitet …", "Preparing …") : t("Meine Daten herunterladen", "Download my data")}</button>
          <button type="button" className="quiet" disabled={!!busy} onClick={() => setOffen(true)}>{t("Mein Konto löschen lassen", "Have my account deleted")}</button>
        </div>
      ) : (
        <>
          <p>{t("Deine Mitgliedschaft in der Agentur endet sofort, deine Anmeldung wird geschlossen und danach gelöscht.", "Your membership in the agency ends immediately, your sign-in is closed and then deleted.")}</p>
          <div className="row">
            <button type="button" className="danger" disabled={!!busy} aria-busy={busy === "loeschen"} onClick={() => void loeschen()}>{busy === "loeschen" ? t("Wird gelöscht …", "Deleting …") : t("Konto endgültig löschen lassen", "Delete account permanently")}</button>
            <button type="button" disabled={!!busy} onClick={() => setOffen(false)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </>
      )}
    </section>
  );
}
